import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Client } from '../client';
import { Contact } from '../contact';
import { Project } from '../project';
import { Invoice } from '../invoice';
import { InvoiceService } from '../invoice.service';
import { faPrint, faCreditCard, faCircleNotch, faMoon, faSun, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { AcceptedProposal } from '../accepted-proposal';
import { NgbAccordionModule, NgbModal, NgbModalRef, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { SafeUrl } from '@angular/platform-browser';
import { Payment } from '../payment';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, lastValueFrom, throwError } from 'rxjs';
import { Address } from '../address';
import { SettingService } from '../setting.service';
import { Site } from '../site';
import { PaymentCategoryPipe } from '../payment-category.pipe';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { SquarePaymentFormComponent } from '../square-payment-form/square-payment-form.component';
import { TinselPayAchPaymentFormComponent } from '../tinsel-pay-ach-payment-form/tinsel-pay-ach-payment-form.component';
import { TinselPayCardPaymentFormComponent } from '../tinsel-pay-card-payment-form/tinsel-pay-card-payment-form.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, CurrencyPipe } from '@angular/common';
import { AlertComponent } from '../alert/alert.component';
import { LocalDarkModeController } from '@main/local-dark-mode.service';
import { VenmoPaymentViewComponent } from '@main/venmo-payment-view/venmo-payment-view.component';
import { Feature } from '@main/feature';
import { ProposalService } from '@main/proposal.service';
import { ProposalPricing } from '@main/proposal-pricing';

@Component({
    selector: 'app-invoice-view',
    templateUrl: './invoice-view.component.html',
    styleUrls: ['./invoice-view.component.css'],
    imports: [AlertComponent, NgIf, FaIconComponent, NgFor, NgbPopover, RouterLink, NgSwitch, NgSwitchCase, TinselPayCardPaymentFormComponent, TinselPayAchPaymentFormComponent, SquarePaymentFormComponent, VenmoPaymentViewComponent, CurrencyPipe, FormatDateTimePipe, PaymentCategoryPipe, NgbAccordionModule]
})
export class InvoiceViewComponent implements OnInit {

  // Modals
  private modalReference: NgbModalRef;

  // Properties
  invoiceLoaded: boolean = false;
  logoUrl: string;
  invoiceId: string;
  invoice: Invoice;
  payments: Payment[] = [];
  totalCreditAmount: number = 0;
  acceptedProposal: AcceptedProposal;
  project: Project;
  site: Site;
  client: Client;
  contacts: Contact[] = [];
  primaryContact: Contact;
  invoiceTerms: string;
  businessAttributes: { name: string, email: string, phone: string, address: Address, displayAddress: boolean };
  proposalPricing: ProposalPricing;

  features: Feature[] =[];
  multiOptionKeys: any[] =[]
  featureHasAtLeastOneImageMap: Map<string, boolean> = new Map<string, boolean>();

  localDarkModeController: LocalDarkModeController;

  // Tinsel Pay
  tinselPayCardPaymentIsEnabled: boolean = false;
  tinselPayAchPaymentIsEnabled: boolean = false;

  // Square
  squarePaymentIsEnabled: boolean = false;

  // Venmo
  venmoPaymentIsEnabled: boolean = false;
  venmoProfileUrl: string;
  venmoCodeExists: boolean = false;
  venmoCodeUrl: string | SafeUrl;

  // Cash
  cashPaymentIsEnabled: boolean = false;
  cashMessage: string;

  // Check
  checkPaymentIsEnabled: boolean = false;
  checkMessage: string;

  // Font Awesome Properties
  faPrint = faPrint;
  faCreditCard = faCreditCard;
  faCircleNotch = faCircleNotch;
  faMoon = faMoon;
  faSun = faSun;
  faQuestionCircle = faQuestionCircle

  constructor(private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private invoiceService: InvoiceService,
    private proposalService: ProposalService,
    private settingService: SettingService) { }

  ngOnInit(): void {
    this.localDarkModeController = new LocalDarkModeController('InvoiceView');
    this.localDarkModeController.darkModeChanged$.subscribe((isDarkModeEnabled) => {
      if (isDarkModeEnabled) {
        document.documentElement.setAttribute('data-bs-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-bs-theme', 'light');
      }
    });
    this.invoiceId = this.route.snapshot.paramMap.get('invoiceId');
    this.printInvoice();
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Modal
  openModal(content: any): void {
    this.modalReference = this.modalService.open(content);
  }

  // Print Invoice
  private printInvoice(): void {
    this.invoiceService.printInvoice(this.invoiceId).pipe(catchError((error: any) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) this.router.navigateByUrl('/404', { replaceUrl: true });
      }
      return throwError(() => new Error('API Request Error'));
    })).subscribe((res) => {
      this.logoUrl = (res.organizationLogoExists) ? this.settingService.getOrganizationLogoUrl(res.invoice.organizationId) : '../assets/TinselLogo.png'
      this.invoice = res.invoice;
      this.payments = res.allPayments;
      this.totalCreditAmount = this.payments.reduce((acc, payment) => { return acc + ((payment.type == 'CREDIT') ? payment.amount : 0); }, 0);
      this.acceptedProposal = res.acceptedProposal;
      this.project = res.project;
      this.site = this.project.site;
      this.client = res.client;
      this.features = res.features;
      this.multiOptionKeys = res.acceptedProposal.multiOptions;
      this.calculateProposalPricing();
      if (res.acceptedProposal.agreementType == 'MULTI') {
        this.multiOptionKeys = Object.keys(res.acceptedProposal.multiOptions);
      }
      this.primaryContact = res.primaryContact;
      if (res.invoiceTerms) {
        let cleanedContent = res.invoiceTerms.replace(/&nbsp;/g, ' ');
        document.getElementById('INVOICE_TERMS').innerHTML = cleanedContent;
      }
      this.businessAttributes = res.businessAttributes;
      // Tinsel Pay
      this.tinselPayCardPaymentIsEnabled = res.paymentSettings.tinselPay.card.isEnabled;
      this.tinselPayAchPaymentIsEnabled = res.paymentSettings.tinselPay.ach.isEnabled;
      // Square
      this.squarePaymentIsEnabled = res.paymentSettings.square.isEnabled;
      // Venmo
      this.venmoPaymentIsEnabled = res.paymentSettings.venmo.isEnabled;
      this.venmoProfileUrl = res.paymentSettings.venmo.url;
      this.venmoCodeExists = res.paymentSettings.venmo.qrCodeExists;
      this.venmoCodeUrl = this.settingService.getVenmoCodeUrl(this.invoice.organizationId);
      // Payment Settings
      this.getPaymentSettings();
      this.invoiceLoaded = true;
    });
  }

  private calculateProposalPricing(): void {
    this.proposalService.calculateProposalPricing(this.acceptedProposal.proposalId).subscribe((res) => {
      this.proposalPricing = res;
    });
  }

  // Get Payment Settings
  private async getPaymentSettings(): Promise<void> {
    // Cash
    const cashSettings = await lastValueFrom(this.settingService.getCashSettings(this.invoice.organizationId));
    this.cashPaymentIsEnabled = cashSettings.isEnabled;
    this.cashMessage = cashSettings.message;
    // Check
    const checkSettings = await lastValueFrom(this.settingService.getCheckSettings(this.invoice.organizationId));
    this.checkPaymentIsEnabled = checkSettings.isEnabled;
    this.checkMessage = checkSettings.message;
  }

  // Payment Successful
  paymentSuccessful(): void {
    this.modalReference.close();
    this.printInvoice();
  }

  // Has Enabled Payment Option
  hasEnabledPaymentOption(): boolean {
    return this.tinselPayCardPaymentIsEnabled ||
      this.tinselPayAchPaymentIsEnabled ||
      this.squarePaymentIsEnabled ||
      this.venmoPaymentIsEnabled ||
      this.cashPaymentIsEnabled ||
      this.checkPaymentIsEnabled;
  }

  multiOptionIsEnabled(key: string): boolean {
    return this.acceptedProposal.multiOptions[key]?.isEnabled;
  }

  featureItemHasImage(featureItem: any): boolean {
    return featureItem.imageType != 'NONE' && (featureItem.imageType == 'ITEM' && featureItem.itemImageExists);
  }

  featureHasAtLeastOneImage(featureItems: any): boolean {
    if (this.featureHasAtLeastOneImageMap.has(featureItems?.featureId)) {
      return this.featureHasAtLeastOneImageMap.get(featureItems?.featureId);
    } else {
      const hasAtLeastOneImage = featureItems.reduce((acc, featureItem) => { return acc || featureItem.imageType != 'NONE' && (featureItem.imageType == 'ITEM' && featureItem.itemImageExists); }, false);
      this.featureHasAtLeastOneImageMap.set(featureItems?.featureId, hasAtLeastOneImage);
      return hasAtLeastOneImage;
    }
  }

  // Print
  print(): void {
    setTimeout(() => {
      window.print();
    }, 500);
  }
}
